import { createFileRoute, Link, Navigate } from "@tanstack/react-router";
import { OVERFLOW_CATEGORIES } from "@/lib/firm-navigation-catalog";
import { MobileModulesDirectory } from "@/components/mobile/MobileModulesSheet";
import { MobileLanguageSwitcher } from "@/components/mobile/MobileLanguageSwitcher";
import { useLanguage } from "@/contexts/LanguageContext";
import { usePhoneChrome } from "@/hooks/use-device-class";
import { Input } from "@/components/ui/input";
import { useMemo, useState } from "react";
import { Search } from "lucide-react";

export const Route = createFileRoute("/mobile/more")({
  head: () => ({ meta: [{ title: "More · AVS ERP" }] }),
  component: MobileMorePage,
});

function MobileMorePage() {
  const { t } = useLanguage();
  const isPhone = usePhoneChrome();
  const [query, setQuery] = useState("");

  if (!isPhone) {
    return <Navigate to="/admin" replace />;
  }

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return OVERFLOW_CATEGORIES;
    return OVERFLOW_CATEGORIES
      .map((cat) => ({
        ...cat,
        items: cat.items.filter(
          (item) => item.label.toLowerCase().includes(q) || item.to.toLowerCase().includes(q),
        ),
      }))
      .filter((cat) => cat.items.length > 0);
  }, [query]);

  return (
    <div className="pb-24 max-w-4xl mx-auto px-4 sm:px-6 pt-2 sm:pt-4 space-y-4">
      <div>
        <h1 className="text-lg font-semibold">{t("mobile.more")}</h1>
        <p className="text-xs text-muted-foreground">{t("mobile.moreSubtitle")}</p>
      </div>

      <div className="relative max-w-lg">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search modules…"
          className="pl-9"
        />
      </div>

      {filtered.map((category) => (
        <section key={category.id} className="max-w-lg">
          <h2 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">
            {category.label}
          </h2>
          <div className="grid grid-cols-2 gap-2">
            {category.items.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.id}
                  to={item.to}
                  search={item.search}
                  className="flex items-center gap-2 rounded-lg border border-border bg-card p-3 min-h-[var(--touch-target)] hover:border-gold/40 active:bg-muted/50"
                >
                  <Icon className="h-4 w-4 text-gold shrink-0" />
                  <span className="text-sm font-medium leading-tight">{item.label}</span>
                </Link>
              );
            })}
          </div>
        </section>
      ))}

      <section className="max-w-lg">
        <div className="rounded-xl border border-border bg-card px-4 py-3">
          <MobileLanguageSwitcher />
        </div>
      </section>

      <section className="max-w-lg pb-6">
        <h2 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">
          {t("mobile.allModules")}
        </h2>
        <MobileModulesDirectory searchable={false} />
      </section>
    </div>
  );
}
